<h2 class="page-subtitle">Register and Update Your User Profile</h2>

<p class="intro">
  After you authenticate through eAuth, CIAO will prompt you to complete your
  user profile. If a user has not logged in yet and needs help with
  authentication, see
  <a [routerLink]="'/help/info/how-to/login-personal-device'"
    >Log In with a Personal Device</a
  >
  or
  <a [routerLink]="'/help/info/how-to/login-government-device'"
    >Log In with a Government Device</a
  >.
</p>

<div class="accordion-controls">
  <button type="button" class="btn-link" (click)="toggleAll()">
    {{ allOpen ? "Collapse All" : "Expand All" }}
  </button>
</div>

<!-- ───────────────────────── Registering ───────────────────────── -->
<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.registering"
    (click)="toggle('registering')"
  >
    <span>Registering Your CIAO User Profile</span>
    <fa-icon [icon]="open.registering ? faChevronUp : faChevronDown"></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.registering">
    <h3>Overview</h3>
    <p>
      When you log in to CIAO for the first time, you&rsquo;ll be guided
      through creating your user profile. Your profile settings determine how
      notifications work and how you are added to teams and trips.
    </p>

    <h3>New CIAO Users</h3>
    <ol>
      <li>
        After authenticating through eAuth, the Welcome to CIAO screen appears.
      </li>
    </ol>
    <img
      class="screenshot"
      [src]="img + '/welcome-yes-no.png'"
      alt="Welcome to CIAO dialog asking &quot;Do you have an existing CIAO account?&quot; with Yes and No buttons"
    />
    <ol start="2">
      <li>Select <strong>No</strong> when asked if you already have a CIAO account.</li>
      <li>
        Complete the registration page:
        <ul>
          <li>First and Last Name</li>
          <li>Government or Personal Cell Phone numbers</li>
          <li>Text notification preferences</li>
          <li>Default Phone</li>
          <li>Default Supervisor (optional)</li>
        </ul>
      </li>
      <li>Select <strong>Create User Profile</strong>.</li>
    </ol>
    <img
      class="screenshot"
      [src]="img + '/registration-form.png'"
      alt="Welcome to CIAO registration form with Add Details, Add Contact, Default Phone and Default Supervisor sections and a Create User Profile button"
    />

    <h3>Returning CIAO Users (Already Have a CIAO Account)</h3>
    <p>
      If you already have an existing CIAO account, <strong>do not</strong>
      register for a new one to avoid having multiple CIAO accounts.
    </p>
    <ol>
      <li>
        After authenticating through eAuth, the Welcome to CIAO screen appears.
        (See the Yes/No screenshot above.)
      </li>
      <li>Select <strong>Yes</strong> when asked if you already have a CIAO account.</li>
      <li>
        A new Welcome to CIAO! window will appear with guidance to add a second
        email to your existing account.
      </li>
    </ol>
    <img
      class="screenshot"
      [src]="img + '/welcome-existing-account.png'"
      alt="Welcome to CIAO! dialog explaining how to add an additional email address to an existing account, with Cancel Registration and Go Back buttons"
    />
    <p>To add an additional email address to your existing account:</p>
    <ol>
      <li>Select <strong>Cancel Registration</strong> to exit CIAO.</li>
      <li>Log in to CIAO using your existing account.</li>
      <li>
        In your profile, enter your additional email (for example, a personal
        Login.gov email) into any blank email field.
      </li>
      <li>Save your profile.</li>
    </ol>
    <p>
      Once completed, you will be able to authenticate into CIAO using both
      email addresses.
    </p>
  </div>
</section>

<!-- ─────────────────────── USDA verification ─────────────────────── -->
<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.verification"
    (click)="toggle('verification')"
  >
    <span>USDA Email Verification (USDA Users Only)</span>
    <fa-icon [icon]="open.verification ? faChevronUp : faChevronDown"></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.verification">
    <p>
      USDA users must verify their USDA email before receiving notifications or
      being added to a team.
    </p>

    <h4>What You May See</h4>
    <ul>
      <li>&ldquo;Unverified USDA Account&rdquo; alert</li>
      <li>Red icon and <strong>Resend verification email</strong></li>
    </ul>

    <h4>To Verify Your Email</h4>
    <ol>
      <li>Select <strong>Resend verification email</strong> if needed.</li>
      <li>Open the message sent to your USDA inbox.</li>
      <li>Select <strong>Verify</strong>.</li>
      <li>A confirmation page will appear in your browser.</li>
    </ol>
    <img
      class="screenshot framed"
      [src]="img + '/header-unverified.png'"
      alt="CIAO header showing the red Unverified USDA account alert with a Resend verification email link beside the user's name"
    />

    <h4>After Verification</h4>
    <ul>
      <li>
        The alert changes to &ldquo;Verified USDA Account&rdquo; when you hover
        over the green checkmark beside your username.
      </li>
    </ul>
    <img
      class="screenshot framed"
      [src]="img + '/header-verified.png'"
      alt="CIAO header showing a green checkmark beside the user's name"
    />

    <p>Non-USDA users do not need to verify their email.</p>
  </div>
</section>

<!-- ───────────────────────── Updating ───────────────────────── -->
<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.updating"
    (click)="toggle('updating')"
  >
    <span>Updating Your User Profile</span>
    <fa-icon [icon]="open.updating ? faChevronUp : faChevronDown"></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.updating">
    <p>You can update your profile anytime.</p>

    <h4>To Open Your Profile</h4>
    <ul>
      <li>Select your name in the upper-right corner.</li>
      <li>On mobile, select the profile icon near <strong>Sign Out</strong>.</li>
    </ul>
    <img
      class="screenshot"
      [src]="img + '/open-profile-desktop-mobile.png'"
      alt="Desktop view highlighting the user's name in the header, and mobile view highlighting the profile icon beside Sign Out"
    />

    <h3>Fields You Can Update</h3>

    <h4>Your Name</h4>
    <ul>
      <li>First and Last Name</li>
    </ul>

    <h4>Admin Roles</h4>
    <ul>
      <li>Admins can assign roles.</li>
      <li>To request Admin access, contact your supervisor.</li>
      <li>Only USDA emails can receive Admin permissions.</li>
    </ul>

    <h4>Default Supervisor</h4>
    <p>CIAO uses three related terms:</p>
    <ul>
      <li>Profile: Default Supervisor</li>
      <li>Teams: Supervisors/Admins</li>
      <li>Trips: Point of Contact (POC)</li>
    </ul>
    <p>
      CIAO will auto-select your POC if your Default Supervisor is also a Team
      Supervisor/Admin.
    </p>

    <h4>Email Addresses</h4>
    <p>
      When you first authenticate into CIAO, eAuth determines which email
      address you used to sign in. CIAO automatically sets this as your
      <strong>primary email</strong> and uses it to create your CIAO account.
    </p>
    <p>You may add additional email addresses, such as:</p>
    <ul>
      <li>USDA.gov email (for USDA device logins)</li>
      <li>Login.gov email (for personal device logins)</li>
    </ul>
    <p>Notes:</p>
    <ul>
      <li>Email notifications are optional.</li>
      <li>
        POC check-in/check-out notifications are sent only to USDA.gov email
        addresses.
      </li>
    </ul>

    <h4>Phone Numbers &amp; Text Notifications</h4>
    <ul>
      <li>Government and personal numbers are optional.</li>
      <li>
        Notification options include:
        <ul>
          <li>15 minutes before a trip is late</li>
          <li>Only when a trip is late</li>
          <li>Every 15 minutes until checked in (up to 24 hours)</li>
        </ul>
      </li>
      <li>If you add multiple numbers, select a default phone.</li>
    </ul>

    <h4>Clock &amp; Time Zone</h4>
    <p>
      These selections only affect the dates and times of email and late text
      notifications.
    </p>
    <ul>
      <li>Choose 12-hour or 24-hour format.</li>
      <li>Choose your preferred time zone.</li>
    </ul>
    <p>Note: CIAO uses your browser location for trip times.</p>

    <h4>Save Your Changes</h4>
    <p>Select <strong>Update User Profile</strong>.</p>
    <img
      class="screenshot"
      [src]="img + '/my-profile-form.png'"
      alt="My Profile form showing name, admin role, default supervisor, email addresses, phone numbers, clock and time zone fields, and an Update User Profile button"
    />
  </div>
</section>

<!-- ───────────────────── Get added to a team ───────────────────── -->
<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.addedToTeam"
    (click)="toggle('addedToTeam')"
  >
    <span>Get Added to a Team</span>
    <fa-icon [icon]="open.addedToTeam ? faChevronUp : faChevronDown"></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.addedToTeam">
    <p>
      Initially, all users begin with read-only access to Teams. You must be
      added to a team by a Supervisor or Administrator to view or create trips.
    </p>
  </div>
</section>

<!-- ─────────────────── Become an admin or supervisor ─────────────────── -->
<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.adminOrSupervisor"
    (click)="toggle('adminOrSupervisor')"
  >
    <span>Become an Admin or Supervisor</span>
    <fa-icon
      [icon]="open.adminOrSupervisor ? faChevronUp : faChevronDown"
    ></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.adminOrSupervisor">
    <p>
      Supervisors and Administrators can elevate a user&rsquo;s CIAO role. CIAO
      users cannot create new accounts for others.
    </p>
    <p>
      Go to the
      <a [routerLink]="'/help/info/admin'">Admin</a> section for management How
      To articles.
    </p>
  </div>
</section>

<hr class="section-divider" />

<div class="related-section">
  <h3>Related Help Center Articles</h3>
  <ul class="related-links">
    <li>
      <a [routerLink]="'/help/info/how-to/login-personal-device'"
        >Log In with a Personal Device</a
      >
    </li>
    <li>
      <a [routerLink]="'/help/info/how-to/login-government-device'"
        >Log In with a Government Device</a
      >
    </li>
    <li>
      <a [routerLink]="'/help/info/admin/teams-create-edit-delete'"
        >Add, Edit, or Delete a Team</a
      >
    </li>
    <li>
      <a [routerLink]="'/help/info/how-to/teams-be-added'">Join a Team</a>
    </li>
  </ul>

  <h3>Knowledge Based Article (KBA)</h3>
  <p>
    <a
      href="https://usdacts-myit.fed.onbmc.com/dwp/app/#/knowledge/KBA00142844/rkm"
      rel="noopener noreferrer"
      target="_blank"
      class="kba-link"
    >
      How to Register and Update Your User Profile
      <mat-icon>arrow_outward</mat-icon>
    </a>
  </p>

  <div class="need-more-help">
    <h4 class="need-more-help-title">Need More Help?</h4>
    <p>
      Visit the
      <a
        href="https://usdacts-myit.fed.onbmc.com/dwp/app/#/"
        rel="noopener noreferrer"
        target="_blank"
        >CEC Digital Workplace</a
      >
      to submit a Customer Help Desk ticket and view other CIAO KBAs.
    </p>
    <p>
      If you have feedback about current or suggested features, email
      <a href="mailto:sm.fs.ciao_feedback@usda.gov">FS-CIAO Feedback</a>.
    </p>
  </div>
</div>
